import React, { useCallback, useState } from 'react';
import { RepresentationType, ThumbnailType } from '../../../../bom/constants';
import { getChildOptionDict, getChildQuestion, getUniqueOptions } from '../../../../bom/selectors';

import AvailableChildOptions from '../AvailableChildOptions';
import CTAButtonWrapper from '../../../common/CTAButtonWrapper';
import Collapse from 'react-bootstrap/Collapse';
import GraphicOptionListbox from '../GraphicOptionListbox';
import IconButtonWrapper from '../../../common/IconButtonWrapper';
import PropTypes from 'prop-types';
import styles from './AdditionalSwatchSelectionMenu.scss';
import { useDebouncedCallback } from 'use-debounce';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

const AdditionalSwatchSelectionMenu = React.forwardRef(({ question, onClose, onSelectOption }, ref) => {
  let { code, questionId, questionLabel, representationType, thumbnailType } = question;

  const uniqueOptions = useSelector((state) => getUniqueOptions(state, code));
  const childQuestion = useSelector((state) => getChildQuestion(state, code));
  const childOptionDict = useSelector((state) => getChildOptionDict(state, !!childQuestion ? childQuestion.code : ''));

  const [options, setOptions] = useState(() =>
    uniqueOptions
      .filter((option) => !option.isSelected && !option.isSoldOut)
      .map((option) => ({ ...option, questionId }))
  );
  const [previewOptionIndex, setPreviewOptionIndex] = useState(-1);
  const [scrollingIntoView, setScrollingIntoView] = useState(false);
  const [showFooter, setShowFooter] = useState(false);

  const { ref: inViewRef, inView } = useInView({
    threshold: 1.0,
    onChange: (inView, entry) => {
      if (inView) {
        console.log('Swatch selection menu completely appeared in view!', entry.target);
        setScrollingIntoView(false);
      } else {
        console.log('Swatch selection menu partially or completely left view!', entry.target);
      }
    },
  });

  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef, ref]
  );

  const listboxId = 'additional-swatch-listbox';
  const labelId = `${listboxId}-label`;

  questionLabel = (questionLabel || '').trim();
  if (questionLabel.endsWith(':')) questionLabel = questionLabel.slice(0, questionLabel.length - 1);

  const hasNoColorReferences = options.every((option) => !option.colorReference);
  const needToUseTexture = options.some((option) => option.useTexture);
  const graphicType =
    thumbnailType === ThumbnailType.LOCAL_IMAGE && hasNoColorReferences
      ? ThumbnailType.LOCAL_IMAGE
      : thumbnailType === ThumbnailType.SCENE7_IMAGE && (hasNoColorReferences || needToUseTexture)
      ? ThumbnailType.SCENE7_IMAGE
      : ThumbnailType.COLOR_SWATCH;

  const previewable = representationType === RepresentationType.PARENT_CHILD;
  const previewOption =
    previewOptionIndex >= 0 ? options[previewOptionIndex] : options.find((option) => option.isSelected);
  const showPreview = previewable && !!previewOption;

  const scrollMenuIntoView = () => {
    if (!!ref && !!ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (!inView) {
        setScrollingIntoView(true);
      }
    }
  };

  const debouncedScrollMenuIntoView = useDebouncedCallback(() => {
    scrollMenuIntoView();
  }, 50);

  const handleMenuBlur = (e) => {
    if (!!ref && !!ref.current && !ref.current.contains(e.relatedTarget)) {
      onClose();
    }
  };

  const handleMenuKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  const handlePreviewOption = (index) => {
    setPreviewOptionIndex(index);
    debouncedScrollMenuIntoView();
  };

  const handleClickOption = (selectedOption) => {
    setOptions(
      options.map((option) => ({
        ...option,
        isSelected: option.codeValue === selectedOption.codeValue,
      }))
    );
    setShowFooter(true);
    scrollMenuIntoView();
  };

  const handleClickContinue = () => {
    const selectedOption = options.find((option) => option.isSelected);
    if (!!selectedOption) {
      const multiSelect = true;
      onSelectOption(multiSelect, { ...selectedOption, isSelected: false });
    }
    onClose();
  };

  return (
    <div
      className={styles['additional-swatch-selection-menu']}
      ref={setRefs}
      tabIndex={-1}
      onBlur={handleMenuBlur}
      onKeyDown={handleMenuKeyDown}>
      <div className={styles['menu-header']}>
        <label id={labelId}>{`Select Another ${questionLabel}`}</label>
        <IconButtonWrapper
          className={styles['close-button']}
          icon="/resources/assets/ui/nextgen/close_20_20.svg"
          description="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles['menu-body']}>
        <GraphicOptionListbox
          aria-labelledby={labelId}
          id={listboxId}
          graphicType={graphicType}
          options={options}
          previewable={previewable}
          scrollable
          scrollingIntoView={scrollingIntoView}
          showPreviewWhenHoveredOutSwatch
          onPreviewOption={previewable ? handlePreviewOption : undefined}
          onSelectOption={handleClickOption}
        />
        {showPreview && (
          <AvailableChildOptions
            heading={`Options Available - ${previewOption.description}`}
            options={childOptionDict[previewOption.codeValue]}
          />
        )}
      </div>

      <Collapse in={showFooter} onEntered={scrollMenuIntoView}>
        <div className={styles['menu-footer']}>
          <div className={styles['buttons-holder']}>
            <CTAButtonWrapper
              className={styles['cancel-button']}
              text="Cancel"
              type="button"
              variant="secondary"
              onClick={onClose}
            />
            <CTAButtonWrapper
              className={styles['continue-button']}
              text="Continue"
              type="button"
              variant="primary"
              onClick={handleClickContinue}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
});

AdditionalSwatchSelectionMenu.propTypes = {
  question: PropTypes.shape({
    code: PropTypes.string.isRequired,
    isRealParentChildQuestion: PropTypes.bool.isRequired,
    itemId: PropTypes.string.isRequired,
    mandatoryFlag: PropTypes.string.isRequired,
    needToReloadUniqueOptions: PropTypes.bool.isRequired,
    questionId: PropTypes.number.isRequired,
    questionLabel: PropTypes.string.isRequired,
    questionText: PropTypes.string.isRequired,
    questionType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
    representationType: PropTypes.oneOf([0, 1]).isRequired,
    sampleQuestionText: PropTypes.string,
    selectionType: PropTypes.oneOf([0, 1]).isRequired,
    sequence: PropTypes.number.isRequired,
    thumbnailType: PropTypes.oneOf([0, 1, 2, 3]).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectOption: PropTypes.func.isRequired,
};

export default AdditionalSwatchSelectionMenu;
